/**
 * Hur en lägenhet identifieras: adress + lägenhetsnummer + postnummer.
 *
 * Tre vägar in i systemet byggde tidigare den nyckeln var för sig, och olika:
 *
 *  • api/housing.ts (ansökan/anspråk) — strippade ", Ort" och "lgh"
 *  • Units.jsx (medlemsimporten)      — strippade bara ", lgh" på slutet
 *  • utils/memberEdit.js (redigering) — bara lowercase och trim
 *
 * Samma fysiska lägenhet fick alltså olika nycklar beroende på vilken väg den
 * kom in — "stormyrtösen 2a, göteborg" från en väg och "stormyrtösen 2a" från
 * en annan matchade aldrig varandra. Det är motorn bakom dubbletterna i
 * medlemsregistret.
 *
 * Reglerna nedan är den mest fullständiga varianten (den ur housing.ts), nu som
 * enda källa. Rent från React och Firestore, så det går att testa direkt.
 */

const asString = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * Gatuadress utan ortstillägg och utan "lgh"-svans.
 * "Stormyrtösen 2A, Göteborg" och "stormyrtösen  2a" ger samma nyckel.
 */
export function normalizeAddress(address: unknown): string {
  return asString(address)
    .replace(/,\s*[^,]+$/, '')   // ", Göteborg" / ", Lgh 1301" — allt efter sista kommat
    .replace(/,\s*lgh\s*$/i, '') // ", Lgh" utan nummer
    .replace(/\s+/g, ' ')        // dubbla mellanslag
    .trim()
    .toLowerCase();
}

/** Lägenhetsnummer utan "lgh"-prefix och utan mellanslag. */
export function normalizeApartmentNumber(apartmentNumber: unknown): string {
  return asString(apartmentNumber)
    .replace(/lgh\s*/gi, '')
    .replace(/\s+/g, '')
    .trim()
    .toLowerCase();
}

/** Postnummer utan mellanslag. "163 51" och "16351" är samma. */
export function normalizeZipCode(zipCode: unknown): string {
  return asString(zipCode).replace(/\s+/g, '').trim();
}

export interface ApartmentFields {
  address?: unknown;
  apartmentNumber?: unknown;
  zipCode?: unknown;
}

/** De tre normaliserade fälten, för lagring på housing-dokumentet. */
export function buildNormalizedFields(fields: ApartmentFields) {
  return {
    normalizedAddress: normalizeAddress(fields.address),
    normalizedApartmentNumber: normalizeApartmentNumber(fields.apartmentNumber),
    normalizedZipCode: normalizeZipCode(fields.zipCode),
  };
}

/**
 * Nyckeln som avgör om två rader beskriver samma lägenhet.
 *
 * Beräknas alltid från råfälten på båda sidor vid jämförelsen — aldrig ur
 * lagrade `normalized*`-fält, som kan vara skrivna av en äldre variant.
 */
export function buildApartmentKey(fields: ApartmentFields): string {
  return [
    normalizeAddress(fields.address),
    normalizeApartmentNumber(fields.apartmentNumber),
    normalizeZipCode(fields.zipCode),
  ].join('|');
}

/**
 * Beskriver två rader samma lägenhet?
 *
 * Adress och lägenhetsnummer måste stämma. Postnumret får bara SKILJA: manuellt
 * inlagda uthyrningar saknar ofta postnummer (Makrillen lgh 1203 i drift), och
 * med den strikta nyckeln ovan hittade de varken sin lägenhet eller spärrade
 * den. Två olika postnummer är två olika lägenheter. Tom adress eller tomt
 * nummer är aldrig en träff — då vet vi inte vilken lägenhet det gäller.
 */
export function isSameApartment(a: ApartmentFields, b: ApartmentFields): boolean {
  const address = normalizeAddress(a.address);
  const apartment = normalizeApartmentNumber(a.apartmentNumber);
  if (!address || !apartment) return false;
  if (address !== normalizeAddress(b.address) || apartment !== normalizeApartmentNumber(b.apartmentNumber)) {
    return false;
  }
  const zipA = normalizeZipCode(a.zipCode);
  const zipB = normalizeZipCode(b.zipCode);
  return !zipA || !zipB || zipA === zipB;
}

/** Sant när nyckeln saknar allt innehåll och därför inte får matcha något. */
export function isEmptyApartmentKey(key: string): boolean {
  return key.replace(/\|/g, '') === '';
}
